﻿import { Directive } from "@app/components/directive";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs } from "@app/helpers/dirs";
import { TouchUtils } from "@tools";

export interface IResizeScope extends ng.IScope {
    handles: string;
    minWidth: string;
    maxWidth: string;
    minHeight: string;
    maxHeight: string;
    resizePosY: boolean;
    resizePosX: boolean;
    disabled: boolean;
}

@NgComponent({
    token: Token.KbResize,
    dependencies: [
    ]
})
export class KbResize extends Directive {
    constructor() {
        super();

        this.restrict = "E";
        this.replace = true;
        this.transclude = true;
        this.scope = {
            handles: "@", // top, bottom, left, right
            minWidth: "@",
            maxWidth: "@",
            minHeight: "@",
            maxHeight: "@",
            resizePosY: "=?", // allow the anchor point (top-left) to move vertically
            resizePosX: "=?", // allow the anchor point (top-left) to move horizontally
            disabled: "=?",
        };
        this.template = Dirs.component("kb-resize");

        this.link = (scope: IResizeScope, elem: ng.IAugmentedJQuery, atts: ng.IAttributes) => {
            let $handles = $();
            let resizing = false;
            let curHandleName = "right";
            let prevSize = {
                width: 0,
                height: 0,
                top: 0,
                left: 0
            };
            let curSize = {
                width: 0,
                height: 0,
                top: 0,
                left: 0
            };
            let startingSize = {
                width: 0,
                height: 0,
                top: 0,
                left: 0
            };
            let startingMousePosition = {
                top: 0,
                left: 0
            };

            let setup = () => {
                if ($handles) {
                    $handles.remove();
                }

                if (!scope.disabled) {
                    setupHandles();
                }
            }

            let setupHandles = () => {
                $handles = $();
                curHandleName = "right";

                let handlesString = scope.handles || "right";
                handlesString.split(",").forEach((h: string) => {
                    let handleName = h.trim();

                    let $handle = $("<div>");
                    $handle.addClass("kb-resize-handle kb-resize-handle-" + handleName);
                    $handle.css({ zIndex: 90 });

                    elem.append($handle);
                    $handles = $handles.add($handle);
                });

                $handles.on("mouseover", (e) => {
                    if (!resizing) {
                        let name;

                        let el = (e.target as HTMLElement);
                        if (el.className) {
                            name = el.className.match(/kb-resize-handle-(top|bottom|left|right)/i);
                        }

                        // the handle we last hover is the one we will move
                        curHandleName = name && name[1] ? name[1] : "right";
                    }
                });
            }

            let mouseInit = () => {
                elem.on("mousedown", mouseDown);
                TouchUtils.addMouseSimulation(elem);
            }

            let mouseDown = (event: JQuery.MouseDownEvent) => {
                if (scope.disabled) {
                    return true;
                }

                let leftMouseBtn = (event.button === 0);
                if (!leftMouseBtn) {
                    return true;
                }

                let handleClicked = false;
                for (let i in $handles) {
                    if ($handles[i] === event.target || $.contains($handles[i], event.target)) {
                        handleClicked = true;
                        break;
                    }
                }

                if (!handleClicked) {
                    return true;
                }

                if (!resizing) {
                    startResizing(event.pageY, event.pageX);
                }

                $(document).on('mousemove', mouseMove);
                $(document).on('mouseup', mouseUp);

                event.preventDefault();
                return true;
            }

            let mouseUp = () => {
                $(document).off('mousemove', mouseMove);
                $(document).off('mouseup', mouseUp);

                resizing = false;

                // we can restore the cursor since we are not resizing
                $("body").css("cursor", "auto");
            }

            let mouseMove = (event: JQuery.MouseMoveEvent) => {
                if (resizing) {
                    mouseDrag(event.pageY, event.pageX);
                } else {
                    startResizing(event.pageY, event.pageX);
                    if (resizing) {
                        mouseDrag(event.pageY, event.pageX);
                    } else {
                        // something happened, abort resize
                        mouseUp();
                    }
                }

                return !resizing;
            }

            let mouseDrag = (mouseY: number, mouseX: number) => {
                prevSize = {
                    width: curSize.width,
                    height: curSize.height,
                    top: curSize.top,
                    left: curSize.left
                };

                let diffY = (mouseY - startingMousePosition.top) || 0;
                let diffX = (mouseX - startingMousePosition.left) || 0;

                let newSize: any = {}
                switch (curHandleName) {
                    case "top":
                        newSize = {
                            height: startingSize.height - diffY,
                            top: scope.resizePosY ? startingSize.top + diffY : startingSize.top
                        };
                        break;
                    case "bottom":
                        newSize = {
                            height: startingSize.height + diffY
                        };
                        break;
                    case "left":
                        newSize = {
                            width: startingSize.width - diffX,
                            left: scope.resizePosX ? startingSize.left + diffX : startingSize.left
                        };
                        break;
                    default: // right
                        newSize = {
                            width: startingSize.width + diffX
                        };
                        break;
                }

                if (!isNaN(parseFloat(newSize.width))) {
                    curSize.width = newSize.width;
                }
                if (!isNaN(parseFloat(newSize.height))) {
                    curSize.height = newSize.height;
                }
                if (!isNaN(parseFloat(newSize.top))) {
                    curSize.top = newSize.top;
                }
                if (!isNaN(parseFloat(newSize.left))) {
                    curSize.left = newSize.left;
                }

                if (curSize.width !== prevSize.width) {
                    elem.css("width", curSize.width + "px");
                }
                if (curSize.height !== prevSize.height) {
                    elem.css("height", curSize.height + "px");
                }
                if (curSize.top !== prevSize.top) {
                    elem.css("top", curSize.top + "px");
                }
                if (curSize.left !== prevSize.left) {
                    elem.css("left", curSize.left + "px");
                }
            }

            let startResizing = (mouseY: number, mouseX: number) => {
                resizing = true;

                let curTop = parseFloat(elem.css("top")) || 0;
                let curLeft = parseFloat(elem.css("left")) || 0;

                curSize = {
                    width: elem.width(),
                    height: elem.height(),
                    top: curTop,
                    left: curLeft
                };

                startingSize = {
                    width: elem.width(),
                    height: elem.height(),
                    top: curTop,
                    left: curLeft
                };

                startingMousePosition = {
                    top: mouseY,
                    left: mouseX
                };

                // keep the cursor type while resizing
                let cursor = $(".kb-resize-handle-" + curHandleName).css("cursor");
                $("body").css("cursor", cursor ?? "auto");
            }

            elem.addClass("kb-resize");
            if (scope.minWidth) elem.css("min-width", scope.minWidth);
            if (scope.maxWidth) elem.css("max-width", scope.maxWidth);
            if (scope.minHeight) elem.css("min-height", scope.minHeight);
            if (scope.maxHeight) elem.css("max-height", scope.maxHeight);

            setup();
            mouseInit();

            scope.$watch("handles", () => {
                setup();
            });

            scope.$watch("disabled", () => {
                setup();
            });

            scope.$on("$destroy", () => {
                if ($handles) {
                    $handles.remove();
                }
                elem.off("mousedown", mouseDown);
                $(document).off('mousemove', mouseMove);
                $(document).off('mouseup', mouseUp);
                TouchUtils.removeMouseSimulation(elem);
            });
        };
    }
}